const PROD_DOMAIN_US = 'simplisafe.com'
const PROD_DOMAIN_UK = 'simplisafe.co.uk'
const STG_DOMAIN_US = 'stg.simplisafe.com'
const STG_DOMAIN_UK = 'stg.simplisafe.co.uk'
const QA_DOMAIN = '.qa.commerce.simplisafe.com'

/**
 * Parses and validates the origin URL
 * @param {string} origin - The origin from the message event
 * @returns {string} The hostname if valid HTTPS origin, empty string otherwise
 *
 * Security: Uses URL parser to prevent attacks like 'https://evil.com@simplisafe.com'
 * where the hostname would correctly parse as 'evil.com'
 */
const parseOrigin = origin => {
  try {
    const parsedUrl = new URL(origin)
    return parsedUrl.protocol === 'https:' ? parsedUrl.hostname : ''
  } catch {
    return ''
  }
}

/**
 * Validates if hostname is a valid subdomain
 */
const isValidSubdomain = (hostname, domain) => {
  return hostname.endsWith(`.${domain}`) && !hostname.includes(`.${domain}.`)
}

/**
 * Checks if hostname is a production environment
 * @param {string} hostname - The hostname to validate
 * @returns {boolean} True if production domain
 */
const isProdEnvironment = hostname => {
  if (hostname === PROD_DOMAIN_US || hostname === PROD_DOMAIN_UK) {
    return true
  }
  return (
    isValidSubdomain(hostname, PROD_DOMAIN_US) ||
    isValidSubdomain(hostname, PROD_DOMAIN_UK)
  )
}

/**
 * Checks if hostname is a staging environment
 * @param {string} hostname - The hostname to validate
 * @returns {boolean} True if staging domain
 */
const isStgEnvironment = hostname => {
  if (hostname === STG_DOMAIN_US || hostname === STG_DOMAIN_UK) {
    return true
  }
  return (
    isValidSubdomain(hostname, STG_DOMAIN_US) ||
    isValidSubdomain(hostname, STG_DOMAIN_UK)
  )
}

/**
 * Checks if hostname is a QA environment
 * @param {string} hostname - The hostname to validate
 * @returns {boolean} True if QA subdomain
 *
 * Allows any subdomain of .qa.commerce.simplisafe.com
 */
const isQAEnvironment = hostname => {
  return hostname.endsWith(QA_DOMAIN)
}

/**
 * Main validation function to check if origin is allowed
 * @param {string} origin - The origin from the message event
 * @returns {boolean} True if origin is from an allowed domain
 *
 * Security: This prevents malicious iframes from sending messages
 * by validating against our allowlist of trusted domains
 */
export const isAllowedOrigin = origin => {
  const hostname = parseOrigin(origin)

  if (!hostname) {
    return false
  }

  return (
    isProdEnvironment(hostname) ||
    isStgEnvironment(hostname) ||
    isQAEnvironment(hostname)
  )
}

/**
 * Validates redirect URLs to prevent open redirect vulnerabilities
 * @param {string} url - The URL to validate
 * @returns {boolean} True if URL is safe to redirect to
 *
 * Only allows HTTPS URLs, relative paths, and hash fragments
 */
export const isValidRedirectUrl = url => {
  if (typeof url !== 'string') {
    return false
  }
  // Reject protocol-relative URLs
  if (url.startsWith('//')) {
    return false
  }
  // Allow HTTPS URLs, relative paths, and hash fragments
  return (
    url.startsWith('https://') || url.startsWith('/') || url.startsWith('#')
  )
}

/**
 * Jebbit Iframe onLoad method
 * Most of this code is provided by the jebbit console,
 * but we have added some additional validation to
 * ensure that the origin is allowed.
 */
export function embedJebbit(t) {
  function e(t) {
    const e =
      t === 'attach' ? window.addEventListener : window.removeEventListener
    e('DOMContentLoaded', i, !1),
      e('load', i, !1),
      e('scroll', i, !1),
      e('resize', i, !1)
  }
  const n = document.querySelector(t)

  function i() {
    ;(function (t) {
      const e = t.getBoundingClientRect?.()
      const n = t.clientHeight / 2
      const i = t.clientWidth / 2

      return (
        e.top >= 0 &&
        e.left >= 0 &&
        e.top <=
          (window.innerHeight || document.documentElement.clientHeight) - n &&
        e.left <=
          (window.innerWidth || document.documentElement.clientWidth) - i
      )
    })(n) && n.contentWindow?.postMessage('startJebbitCampaign', '*')
  }
  window.addEventListener(
    'message',
    function (t) {
      if (!t?.origin || !isAllowedOrigin(t.origin)) {
        return
      }

      t &&
        (t.data === 'jebbitSessionCreated'
          ? e('remove')
          : t.data === 'jebbitCampaignLoaded'
            ? i()
            : t.data &&
              t.data.action === 'redirect' &&
              t.data.options?.link &&
              isValidRedirectUrl(t.data.options.link) &&
              (window.location.href = t.data.options.link))
    },
    !1
  ),
    e('attach')
}
